import React, { useEffect, useRef } from 'react';
import '../styles/fmp_landing.css';
import logoComece from '../assets/logo-comece.svg';

const A = `${process.env.PUBLIC_URL || ''}/fmp-assets`;

const fmtBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(n);

const Ck = () => (
  <span className="ck"><i className="fas fa-check" aria-hidden="true"></i></span>
);

const FmpLanding = () => {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const cleanups = [];

    // ---- reveal on scroll (com stagger via data-delay) ----
    const reveals = root.querySelectorAll('.fmp-reveal');
    if (reduce || !('IntersectionObserver' in window)) {
      reveals.forEach((el) => el.classList.add('in'));
    } else {
      const io = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }),
        { threshold: 0.14 }
      );
      reveals.forEach((el) => io.observe(el));
      cleanups.push(() => io.disconnect());
    }

    // ---- count-up ----
    const fmtFor = (el, v) => {
      const mode = el.getAttribute('data-fmt');
      if (mode === 'brl') return fmtBRL(v);
      const dec = parseInt(el.getAttribute('data-dec') || '0', 10);
      return v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
    };
    const runCount = (el) => {
      const target = parseFloat(el.getAttribute('data-count')) || 0;
      const pre = el.getAttribute('data-pre') || '';
      const suf = el.getAttribute('data-suf') || '';
      if (reduce) { el.textContent = pre + fmtFor(el, target) + suf; return; }
      const dur = 1500; let start = null;
      const tick = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = pre + fmtFor(el, target * eased) + suf;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const counters = root.querySelectorAll('[data-count]');
    if ('IntersectionObserver' in window && !reduce) {
      const cio = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) { runCount(e.target); cio.unobserve(e.target); } }),
        { threshold: 0.6 }
      );
      counters.forEach((c) => cio.observe(c));
      cleanups.push(() => cio.disconnect());
    } else {
      counters.forEach(runCount);
    }

    // ---- sparkline draw ----
    if (!reduce) {
      const line = root.querySelector('.fmp-spark-line');
      if (line) {
        const len = line.getTotalLength();
        line.style.strokeDasharray = len; line.style.strokeDashoffset = len;
        line.style.transition = 'stroke-dashoffset 1.7s ease .4s';
        requestAnimationFrame(() => { line.style.strokeDashoffset = '0'; });
      }
    }

    // ---- scroll progress + nav shrink ----
    const prog = root.querySelector('.fmp-progress');
    const nav = root.querySelector('.fmp-nav');
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const pct = max > 0 ? (h.scrollTop / max) * 100 : 0;
      if (prog) prog.style.width = pct + '%';
      if (nav) nav.classList.toggle('scrolled', h.scrollTop > 30);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    cleanups.push(() => window.removeEventListener('scroll', onScroll));

    // ---- tilt 3D ----
    if (!reduce && window.matchMedia('(pointer:fine)').matches) {
      root.querySelectorAll('[data-tilt]').forEach((el) => {
        const onMove = (ev) => {
          const r = el.getBoundingClientRect();
          const px = (ev.clientX - r.left) / r.width - 0.5;
          const py = (ev.clientY - r.top) / r.height - 0.5;
          el.style.transform = `perspective(900px) rotateX(${(-py * 6).toFixed(2)}deg) rotateY(${(px * 8).toFixed(2)}deg) translateY(-4px)`;
        };
        const onLeave = () => { el.style.transform = ''; };
        el.addEventListener('mousemove', onMove);
        el.addEventListener('mouseleave', onLeave);
        cleanups.push(() => { el.removeEventListener('mousemove', onMove); el.removeEventListener('mouseleave', onLeave); });
      });

      // ---- parallax aurora (segue o mouse no hero) ----
      const hero = root.querySelector('.fmp-hero');
      const aurora = root.querySelector('.fmp-hero .fmp-aurora');
      if (hero && aurora) {
        const onHero = (ev) => {
          const r = hero.getBoundingClientRect();
          const px = (ev.clientX - r.left) / r.width - 0.5;
          const py = (ev.clientY - r.top) / r.height - 0.5;
          aurora.style.transform = `translate(${px * 26}px, ${py * 26}px)`;
        };
        hero.addEventListener('mousemove', onHero);
        cleanups.push(() => hero.removeEventListener('mousemove', onHero));
      }
    }

    // ---- videos play/pause conforme visibilidade ----
    const vids = root.querySelectorAll('video[data-inview]');
    if ('IntersectionObserver' in window) {
      const vio = new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          const v = e.target;
          if (e.isIntersecting) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
          else v.pause();
        }),
        { threshold: 0.35 }
      );
      vids.forEach((v) => vio.observe(v));
      cleanups.push(() => vio.disconnect());
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  const goDemo = () => { window.location.href = 'mailto:eleandro.martins@comecehub.com.br?subject=Demonstra%C3%A7%C3%A3o%20FMP'; };

  return (
    <div className="fmp-site" ref={rootRef}>
      <div className="fmp-progress" />

      {/* ===================== NAV ===================== */}
      <nav className="fmp-nav">
        <div className="fmp-wrap fmp-nav-inner">
          <a className="fmp-brand" href="#topo" aria-label="FMP — by Comece">
            <img className="fmp-brand-logo" src={logoComece} alt="Comece" />
            <span className="fmp-brand-sep" aria-hidden="true" />
            <span className="fmp-brand-tag">FMP</span>
          </a>
          <div className="fmp-nav-links">
            <a href="#produto">Produto</a>
            <a href="#modulos">Módulos</a>
            <a href="#ia">Inteligência</a>
            <a href="#como">Como funciona</a>
          </div>
          <button className="fmp-btn navy" onClick={goDemo}>Agendar demonstração</button>
        </div>
      </nav>

      {/* ===================== HERO ===================== */}
      <header className="fmp-hero" id="topo">
        <div className="fmp-aurora" aria-hidden="true">
          <span className="fmp-blob b1" /><span className="fmp-blob b2" />
          <span className="fmp-blob b3" /><span className="fmp-blob b4" />
        </div>
        <div className="fmp-hero-grid-bg" aria-hidden="true" />
        <div className="fmp-wrap fmp-hero-inner">
          <div>
            <span className="fmp-eyebrow fmp-reveal in">Financial Management Platform</span>
            <h1 className="fmp-reveal" style={{ transitionDelay: '80ms' }}>
              Tudo que seu CFO precisa saber sobre <span className="grad">a saúde da sua empresa</span>
            </h1>
            <p className="lead fmp-reveal" style={{ transitionDelay: '160ms' }}>
              A plataforma de gestão financeira e CaaS da Comece Hub conecta o seu ERP e transforma extrato,
              contas a pagar e a receber em decisão — com posição semanal, orçamento, Saving e projeção por inteligência artificial.
            </p>
            <div className="fmp-hero-cta fmp-reveal" style={{ transitionDelay: '240ms' }}>
              <button className="fmp-btn" onClick={goDemo}>
                Agendar demonstração <i className="fas fa-arrow-right"></i>
              </button>
              <a className="fmp-btn ghost" href="#modulos">Ver os módulos</a>
            </div>
            <div className="fmp-hero-trust fmp-reveal" style={{ transitionDelay: '320ms' }}>
              <span className="dot" /> <span>Integração nativa com</span>
              <span className="fmp-erp-badge"><img src={`${A}/images/omie.webp`} alt="Omie" /></span>
              <span className="fmp-erp-badge"><img src={`${A}/images/conta-azul.webp`} alt="Conta Azul" /></span>
            </div>
          </div>

          {/* device mock */}
          <div className="fmp-hero-mock fmp-reveal" style={{ transitionDelay: '200ms' }}>
            <div className="fmp-device" data-tilt>
              <div className="bar"><i /><i /><i /><span>fmp.comecehub.com.br</span></div>
              <div className="fmp-panel">
                <div className="ph"><span className="t">Saldo consolidado · todas as contas</span><span className="fmp-pill green">+8,4% no mês</span></div>
                <div className="fmp-balance fmp-num" data-count="1284530" data-fmt="brl">R$ 0</div>
                <div className="fmp-balance-sub"><span className="up fmp-num">▲ R$ 99.420</span><span>vs. mês anterior · 6 contas</span></div>
                <svg className="fmp-spark" viewBox="0 0 320 54" preserveAspectRatio="none" aria-hidden="true">
                  <defs><linearGradient id="fmpsg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#3fd093" stopOpacity=".45" /><stop offset="1" stopColor="#3fd093" stopOpacity="0" />
                  </linearGradient></defs>
                  <path d="M0,42 L40,38 L80,40 L120,28 L160,31 L200,20 L240,24 L280,12 L320,9 L320,54 L0,54 Z" fill="url(#fmpsg)" />
                  <path className="fmp-spark-line" d="M0,42 L40,38 L80,40 L120,28 L160,31 L200,20 L240,24 L280,12 L320,9"
                    fill="none" stroke="#3fd093" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="fmp-panel">
                <div className="ph"><span className="t">Saldo por conta</span><span className="fmp-pill cyan">Projeção IA</span></div>
                <div className="fmp-minibars" aria-hidden="true">
                  <div className="c"><div className="bar" style={{ height: '48%' }} /><small>CC</small></div>
                  <div className="c"><div className="bar cy" style={{ height: '72%' }} /><small>Invest</small></div>
                  <div className="c"><div className="bar" style={{ height: '36%' }} /><small>Caixa</small></div>
                  <div className="c"><div className="bar gd" style={{ height: '92%' }} /><small>Proj.</small></div>
                </div>
              </div>
            </div>
            <div className="fmp-float f2"><span className="ic cyan"><i className="fas fa-bolt"></i></span>
              <div><div className="k">Dados do ERP</div><div className="v">Tempo real</div></div></div>
          </div>
        </div>
      </header>

      {/* ===================== O PRODUTO (bento) ===================== */}
      <section className="fmp-sec soft" id="produto">
        <div className="fmp-wrap">
          <div className="fmp-head fmp-reveal">
            <span className="fmp-eyebrow">O produto</span>
            <h2>Uma plataforma inteira de gestão financeira — em uma só tela.</h2>
            <p>O FMP reúne o que antes vivia em planilhas soltas: saldos, projeção, orçamento, negociação de
              fornecedores e fechamento. Os dados chegam do ERP, a plataforma calcula e você recebe clareza.</p>
          </div>
          <div className="fmp-bento">
            <div className="fmp-cell feature wide tall fmp-reveal" data-tilt>
              <span className="glow" aria-hidden="true" />
              <div>
                <span className="fmp-eyebrow">Tudo conectado</span>
                <h3 style={{ marginTop: 14 }}>Do dado do banco à decisão financeira</h3>
                <p>Sincroniza com o seu ERP, consolida todas as contas e atualiza em tempo real — sem digitação, sem planilha no meio.</p>
              </div>
              <div>
                <div className="fmp-accounts-cap"><i className="fas fa-layer-group"></i> Conecte quantas contas quiser</div>
                <div className="fmp-accounts">
                  <div className="fmp-arow"><span><span className="fmp-bank"><img src={`${A}/images/itau.png`} alt="Itaú" /></span> Itaú</span><b className="fmp-num">R$ 86.000,00</b></div>
                  <div className="fmp-arow"><span><span className="fmp-bank"><img src={`${A}/images/santander.png`} alt="Santander" /></span> Santander</span><b className="fmp-num">R$ 20.000,00</b></div>
                  <div className="fmp-arow"><span><span className="fmp-bank"><img src={`${A}/images/nubank.png`} alt="Nubank" /></span> Nubank</span><b className="fmp-num neg">- R$ 10.000,00</b></div>
                </div>
                <div className="fmp-liq">
                  <div><span>Saldo líquido</span><b className="fmp-num">R$ 96.000,00</b></div>
                  <small>disponível para pagar as contas da semana</small>
                </div>
              </div>
            </div>
            <div className="fmp-cell fmp-reveal" style={{ transitionDelay: '60ms' }}>
              <div className="ic navy"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="6" rx="7.5" ry="3" /><path d="M4.5 6v6c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3V6" /><path d="M4.5 12v6c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3v-6" /></svg></div>
              <h3>Saldo consolidado</h3><p>Todas as contas e investimentos somados, com variação real do mês.</p></div>
            <div className="fmp-cell fmp-reveal" style={{ transitionDelay: '120ms' }}>
              <div className="ic cyan"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2.5" /><path d="M3.5 9.5h17M8 3v4M16 3v4" /><path d="M7.5 14h4" /></svg></div>
              <h3>Posição semanal</h3><p>Saldo projetado com o que vence e o que entra — e quanto você tem para pagar as contas.</p></div>
            <div className="fmp-cell fmp-reveal" style={{ transitionDelay: '60ms' }}>
              <div className="ic gold"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /></svg></div>
              <h3>Orçado x realizado</h3><p>Meta por categoria e alerta de estouro, mês a mês.</p></div>
            <div className="fmp-cell fmp-reveal" style={{ transitionDelay: '120ms' }}>
              <div className="ic green"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v11" /><path d="M8 10l4 4 4-4" /><path d="M5 20h14" /></svg></div>
              <h3>Saving</h3><p>Monitora reajustes de fornecedores e negocia contratos anuais para conseguir descontos — virando economia realizada.</p></div>
            <div className="fmp-cell wide fmp-reveal" style={{ transitionDelay: '60ms' }}>
              <div className="ic purple"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 16l5-5 4 3 5-6" /><path d="M18.4 3l.75 2.05 2.05.75-2.05.75L18.4 8.6l-.75-2.05-2.05-.75 2.05-.75z" fill="currentColor" stroke="none" /></svg></div>
              <h3>Projeção por IA</h3><p>Modelos preditivos estimam entradas, saídas e saldo dos próximos meses — para você agir antes do <strong style={{ color: 'var(--danger)', fontWeight: 700 }}>problema</strong> acontecer.</p></div>
            <div className="fmp-cell fmp-reveal" style={{ transitionDelay: '120ms' }}>
              <div className="ic navy"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="4.5" width="14" height="16.5" rx="2.5" /><path d="M9 4.5h6V7H9z" /><path d="M8.7 13.2l2.3 2.3 4-4.5" /></svg></div>
              <h3>Fechamento</h3><p>Checklist e envio à contabilidade com prévia revisada.</p></div>
            <div className="fmp-cell fmp-reveal" style={{ transitionDelay: '60ms' }}>
              <div className="ic gold"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3.5" y="4" width="17" height="16" rx="2.5" /><path d="M3.5 9h17M10 9v11M16 9v11" /></svg></div>
              <h3>DRE vertical &amp; horizontal</h3><p>Demonstração do resultado completa: hierarquia nas linhas ou os meses lado a lado nas colunas.</p></div>
          </div>
        </div>
      </section>

      {/* ===================== MÓDULOS (vídeos) ===================== */}
      <section className="fmp-sec" id="modulos">
        <div className="fmp-wrap">
          <div className="fmp-head fmp-reveal" style={{ marginBottom: 8 }}>
            <span className="fmp-eyebrow">Módulos em ação</span>
            <h2>Veja a plataforma funcionando de verdade.</h2>
          </div>

          {/* Dashboard */}
          <div className="fmp-mod">
            <div className="fmp-mod-copy fmp-reveal">
              <span className="fmp-mod-tag">DASHBOARD FINANCEIRO</span>
              <h3>O retrato consolidado do caixa, conta a conta.</h3>
              <p>Saldo disponível, investimentos e variação real — somados de todas as contas e cruzados com a previsão da IA.</p>
              <ul className="fmp-feat">
                <li><Ck />Saldos por conta, diversificação e evolução acumulada</li>
                <li><Ck />Fluxo de caixa mensal e diário da conta corrente</li>
                <li><Ck />Atalhos para DRE, projetos, contas a pagar e receber</li>
              </ul>
            </div>
            <div className="fmp-frame brand fmp-reveal" data-tilt style={{ transitionDelay: '100ms' }}>
              <span className="tag">Saldos por conta</span>
              <video data-inview muted loop playsInline autoPlay preload="metadata"
                poster={`${A}/images/01_dashboard_financeiro.png`}>
                <source src={`${A}/videos/01_saldos_por_conta.mp4`} type="video/mp4" />
              </video>
            </div>
          </div>

          {/* Planejamento */}
          <div className="fmp-mod flip">
            <div className="fmp-mod-copy fmp-reveal">
              <span className="fmp-mod-tag">PLANEJAMENTO ORÇAMENTÁRIO</span>
              <h3>Orçado contra realizado, mês a mês.</h3>
              <p>Defina a meta por categoria e acompanhe o consumo do orçamento, o que ultrapassou e os gastos não previstos — com DRE vertical e horizontal.</p>
              <ul className="fmp-feat">
                <li><Ck />Orçamento de despesas e de receitas por categoria</li>
                <li><Ck />DRE completo (YTD), vertical e horizontal</li>
                <li><Ck />Análise automática das categorias que mais estouraram</li>
              </ul>
            </div>
            <div className="fmp-frame fmp-reveal" data-tilt style={{ transitionDelay: '100ms' }}>
              <span className="tag">Orçado x Realizado</span>
              <video data-inview muted loop playsInline autoPlay preload="metadata"
                poster={`${A}/images/06_planejamento_orcamentario.png`}>
                <source src={`${A}/videos/03_orcado_x_realizado.mp4`} type="video/mp4" />
              </video>
            </div>
          </div>

          {/* Saving */}
          <div className="fmp-mod">
            <div className="fmp-mod-copy fmp-reveal">
              <span className="fmp-mod-tag">SAVING — CONTROLE DE PAGAMENTOS</span>
              <h3>Quem reajustou — e quanto dá para economizar.</h3>
              <p>O Saving monitora os fornecedores recorrentes, aponta os reajustes e calcula o impacto no ano. Cada oportunidade entra numa esteira de negociação até virar economia realizada.</p>
              <ul className="fmp-feat">
                <li><Ck />Variação acumulada, YoY e projeção anual por fornecedor</li>
                <li><Ck />Esteiras de renegociação por aumento e contrato anual</li>
                <li><Ck />E-mail de negociação e registro da economia realizada</li>
              </ul>
            </div>
            <div className="fmp-frame brand fmp-reveal" data-tilt style={{ transitionDelay: '100ms' }}>
              <span className="tag">Esteira de negociação</span>
              <img src={`${A}/images/05_saving_esteira.png`} alt="Saving — esteira de renegociação" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== IA ===================== */}
      <section className="fmp-sec dark" id="ia">
        <div className="fmp-aurora" aria-hidden="true" style={{ opacity: .5 }}>
          <span className="fmp-blob b2" /><span className="fmp-blob b4" />
        </div>
        <div className="fmp-wrap fmp-ia-grid" style={{ position: 'relative' }}>
          <div className="fmp-reveal">
            <span className="fmp-eyebrow gold">Análise preditiva</span>
            <h2 style={{ color: '#fff', marginTop: 16 }}>A IA projeta o caixa antes de ele acontecer.</h2>
            <p style={{ color: 'var(--on-dark-mut)', fontSize: 17.5, marginTop: 16 }}>
              Sobre o histórico de movimentos, os modelos estimam entradas, saídas e saldo líquido dos próximos
              meses — e apontam a tendência em linguagem clara, para virar conversa com o cliente.
            </p>
            <div className="fmp-ia-tags">
              <span className="t">Modelo: <b>Random Forest</b></span>
              <span className="t">Modelo: <b>Regressão Linear</b></span>
              <span className="t">Histórico <b>vs.</b> previsão</span>
            </div>
          </div>
          <div className="fmp-frame brand fmp-reveal" data-tilt style={{ transitionDelay: '100ms' }}>
            <span className="tag">Evolução do saldo · IA</span>
            <video data-inview muted loop playsInline autoPlay preload="metadata"
              poster={`${A}/images/02_evolucao_saldo.png`}>
              <source src={`${A}/videos/02_evolucao_saldo_ia.mp4`} type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* ===================== GALERIA ===================== */}
      <section className="fmp-sec soft" id="galeria">
        <div className="fmp-wrap">
          <div className="fmp-head center fmp-reveal">
            <span className="fmp-eyebrow">Telas reais</span>
            <h2>Cada módulo, em alta definição.</h2>
            <p>Capturas reais da plataforma — com dados ilustrativos e protegidos.</p>
          </div>
          <div className="fmp-bento" style={{ gridAutoRows: 'auto' }}>
            {[
              ['01_dashboard_financeiro.png', 'Dashboard Financeiro', 'wide'],
              ['03_analise_investimentos.png', 'Análise de Investimentos', ''],
              ['04_saving_economia.png', 'Saving — Economia', ''],
              ['02_evolucao_saldo.png', 'Evolução do Saldo · IA', ''],
              ['07_dre_vertical.png', 'DRE Vertical', ''],
              ['08_dre_horizontal.png', 'DRE Horizontal', 'wide'],
            ].map(([file, label, cls], i) => (
              <figure key={file} className={`fmp-frame fmp-reveal ${cls === 'wide' ? 'fmp-cell-wide' : ''}`}
                data-tilt style={{ transitionDelay: `${(i % 3) * 80}ms`, gridColumn: cls === 'wide' ? 'span 2' : 'auto', margin: 0, padding: 0 }}>
                <span className="tag">{label}</span>
                <img src={`${A}/images/${file}`} alt={label} loading="lazy" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== COMO FUNCIONA ===================== */}
      <section className="fmp-sec" id="como">
        <div className="fmp-wrap">
          <div className="fmp-head center fmp-reveal">
            <span className="fmp-eyebrow">Como funciona</span>
            <h2>Três passos, do ERP ao cliente.</h2>
          </div>
          <div className="fmp-steps">
            {[
              ['fa-plug', 'Conecta', 'O FMP integra ao Omie ou ao Conta Azul e importa contas, extratos, títulos e categorias automaticamente.'],
              ['fa-gears', 'Calcula', 'Consolida saldos, projeta a posição da semana, compara orçado x realizado e roda a previsão da IA.'],
              ['fa-paper-plane', 'Comunica', 'O operador revisa a prévia e envia posição e fechamento por e-mail — o cliente recebe um retrato claro e acionável.'],
            ].map(([ic, t, d], i) => (
              <div key={t} className="fmp-step fmp-reveal" style={{ transitionDelay: `${i * 90}ms` }}>
                <div className="n">{i + 1}</div>
                <h3><i className={`fas ${ic}`} style={{ color: 'var(--cyan)', marginRight: 10 }}></i>{t}</h3>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== RESULTADOS ===================== */}
      <section className="fmp-sec dark">
        <div className="fmp-wrap">
          <div className="fmp-head center fmp-reveal">
            <span className="fmp-eyebrow gold">O que muda na rotina</span>
            <h2 style={{ color: '#fff' }}>Menos planilha, mais decisão.</h2>
          </div>
          <div className="fmp-stats fmp-reveal">
            <div className="cell"><div className="v fmp-num" data-count="5" data-suf="+">0</div><div className="k">módulos integrados em uma só tela</div></div>
            <div className="cell"><div className="v fmp-num" data-count="2">0</div><div className="k">ERPs conectados nativamente</div></div>
            <div className="cell"><div className="v fmp-num" data-count="7" data-suf=" dias">0</div><div className="k">de horizonte na posição semanal</div></div>
            <div className="cell"><div className="v fmp-num" data-count="12" data-suf=" meses">0</div><div className="k">de projeção orçamentária por ano</div></div>
          </div>
        </div>
      </section>

      {/* ===================== CTA ===================== */}
      <section className="fmp-cta" id="contato">
        <div className="fmp-aurora" aria-hidden="true">
          <span className="fmp-blob b1" /><span className="fmp-blob b3" />
        </div>
        <div className="fmp-wrap fmp-cta-inner">
          <span className="fmp-eyebrow gold" style={{ justifyContent: 'center' }}>Comece Hub</span>
          <h2 style={{ marginTop: 16 }}>Mostre ao seu cliente o caixa dele como ele nunca viu.</h2>
          <p>Agende uma demonstração do FMP e veja a posição, o orçamento e a projeção do seu próprio cliente em tempo real.</p>
          <div className="fmp-hero-cta">
            <button className="fmp-btn" onClick={goDemo}>Agendar demonstração <i className="fas fa-arrow-right"></i></button>
            <a className="fmp-btn ghost" href="#modulos">Rever os módulos</a>
          </div>
        </div>
      </section>

      <footer className="fmp-footer">
        <div className="fmp-wrap fmp-footer-inner">
          <span><b>FMP</b> · Financial Management Platform — Comece Hub</span>
          <span className="fmp-num" style={{ opacity: .7 }}>Contabilidade consultiva · BPO Financeiro · BI</span>
        </div>
      </footer>
    </div>
  );
};

export default FmpLanding;
